import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  NOTARY_PROVINCE_LANDINGS,
  getNotaryProvinceLanding,
  type NotaryProvinceLanding,
} from "@/content/notary-corpus/provinces";
import { NOTARY_DOC_LANDINGS } from "@/content/notary-corpus/documents";

function localFaqsEn(p: NotaryProvinceLanding) {
  return [
    {
      q: `Where can I find a notary public in ${p.provinceEn}?`,
      a: `${p.logisticsEn} Documents are certified by a Notarial Services Attorney registered with the Lawyers Council of Thailand under the 2008 regulation on the registration of notarial services attorneys; you can verify the licence before instructing us.`,
    },
    { q: `Which documents are most often notarised in ${p.provinceEn}?`, a: p.topDocsEn.join(" · ") },
    { q: `How long does notarisation take in ${p.provinceEn}?`, a: p.turnaroundEn },
    {
      q: `How are notary fees calculated in ${p.provinceEn}?`,
      a: "Fees depend on the number of documents and seals required. Official MFA legalisation fees and embassy attestation fees are charged at the authority's published rates, separately from our professional fee. A written quote is always provided before we start.",
    },
    {
      q: "Do I have to sign in person?",
      a: "Yes for signature certification — you must sign in front of the attorney with your original passport or Thai ID. Pre-signed documents must be re-signed. For certified true copies you must present the original document.",
    },
    {
      q: `Do I need to travel to Bangkok for MFA legalisation after notarising in ${p.provinceEn}?`,
      a: "No. We can be authorised to lodge the documents with the Legalisation Division of the Department of Consular Affairs at Chaeng Watthana, coordinate the destination embassy, and return the documents by registered mail or courier.",
    },
    {
      q: "Can Thailand issue an apostille?",
      a: "Not yet. Thailand is not a party to the Hague Apostille Convention, so Thai documents used abroad follow the MFA legalisation route and then attestation at the destination country's embassy or consulate. Please confirm the current status at hcch.net and consular.mfa.go.th before proceeding.",
    },
    {
      q: "Is a Thai notarial services attorney equivalent to a common-law notary public?",
      a: "Thailand has no common-law notary public office. Instead, a registered Notarial Services Attorney performs signature witnessing, certified copies and affidavits, and this certification is accepted by foreign authorities once it has passed MFA legalisation.",
    },
    {
      q: "Do you guarantee the receiving authority will accept the document?",
      a: "No — acceptance is at the discretion of the receiving authority. We check the format, the spelling of names against your passport, document validity periods and the order of certification beforehand, and we redo the work at no charge if the error is ours.",
    },
  ];
}

export const Route = createFileRoute("/en/notary/province/$province")({
  loader: ({ params }) => {
    const p = getNotaryProvinceLanding(params.province);
    if (!p) throw notFound();
    return { p };
  },
  component: NotaryProvincePageEN,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Province not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { p } = loaderData;
    const title = `Notary Public ${p.provinceEn} 2026 — Notarial Services Attorney`;
    const description = `Notarial services attorney in ${p.provinceEn}: powers of attorney, affidavits and certified true copies, plus MFA legalisation and embassy attestation handled end to end.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/notary/province/${params.province}`,
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: `/notary/province/${params.province}` }],
        keywords: [
          `notary public ${p.provinceEn.toLowerCase()}`,
          `notary ${p.provinceEn.toLowerCase()} thailand`,
          `power of attorney notarisation ${p.provinceEn.toLowerCase()}`,
          `certified true copy ${p.provinceEn.toLowerCase()}`,
          `notarial services attorney ${p.provinceEn.toLowerCase()}`,
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Notary Public", url: `${site}/en/notary` },
              { name: `Notary Public ${p.provinceEn}`, url: `${site}/en/notary/province/${params.province}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(localFaqsEn(p))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/notary/province/${params.province}`)) },
      ],
    };
  },
});

function NotaryProvincePageEN() {
  const { p } = Route.useLoaderData();
  const faqs = localFaqsEn(p);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/notary" className="hover:underline">Notary Public</a> <span>/</span>{" "}
          <span>{p.provinceEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Notary Public in {p.provinceEn} — Notarial Services Attorney (2026)
        </h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">{p.demandEn}</p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Free quote →</a>
          <a href="/en/notary/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Notary FAQ</a>
          <a href={`/notary/province/${p.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Most requested notarisations in {p.provinceEn}</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {p.topDocsEn.map((d: string, i: number) => <li key={i}>{d}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">Appointments and document handling in {p.provinceEn}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{p.logisticsEn}</p>
          <p className="mt-4 text-sm text-muted-foreground"><strong>Turnaround:</strong> {p.turnaroundEn}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">Certification chain for Thai documents used abroad</h2>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            <li>A registered notarial services attorney witnesses your signature or certifies the true copy (you must sign in person)</li>
            <li>Translation into the destination language by a translator the receiving authority accepts, if required</li>
            <li>Legalisation at the Legalisation Division, Department of Consular Affairs, MFA (Chaeng Watthana)</li>
            <li>Attestation at the destination country's embassy or consulate in Thailand, where required</li>
          </ol>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Notary FAQ — {p.provinceEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Notarisation by document type</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_DOC_LANDINGS.map((x) => (
              <a key={x.slug} href={`/en/notary/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other service areas</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_PROVINCE_LANDINGS.filter((x) => x.slug !== p.slug).map((x) => (
              <a key={x.slug} href={`/en/notary/province/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.provinceEn}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Professional framework: Lawyers Council of Thailand regulation on the registration of notarial services attorneys, B.E. 2551 (2008) ·
          Legalisation procedure per the Department of Consular Affairs (consular.mfa.go.th) · Hague Convention status per hcch.net —
          fees and processing times can change; always confirm with the responsible authority before proceeding.
        </p>

        <FaqSourceNote topic="notary" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
